/*
|--------------------------------------------------------------------------
| FORMAT LABEL
|--------------------------------------------------------------------------
*/

export const formatEmployeeLabel = (
    value
) => {

    return String(
        value ||
        ""
    )
        .replace(
            /[-.]/g,
            "_"
        )
        .toLowerCase()
        .split(
            "_"
        )
        .filter(
            Boolean
        )
        .map(
            (
                word
            ) =>
                word
                    .charAt(0)
                    .toUpperCase() +
                word.slice(1)
        )
        .join(" ");

};


/*
|--------------------------------------------------------------------------
| OBJECT ID
|--------------------------------------------------------------------------
*/

export const getObjectId = (
    value
) => {

    if (
        !value
    ) {
        return "";
    }


    if (
        typeof value ===
        "string"
    ) {
        return value;
    }


    return String(
        value?._id ||
        value?.id ||
        ""
    );

};


/*
|--------------------------------------------------------------------------
| DATE INPUT
|--------------------------------------------------------------------------
*/

export const formatEmployeeDateInput = (
    value
) => {

    if (
        !value
    ) {
        return "";
    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }


    return date
        .toISOString()
        .slice(
            0,
            10
        );

};


/*
|--------------------------------------------------------------------------
| DISPLAY DATE
|--------------------------------------------------------------------------
*/

export const formatEmployeeDate = (
    value
) => {

    if (
        !value
    ) {
        return "-";
    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }


    return date
        .toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );

};


/*
|--------------------------------------------------------------------------
| CURRENCY
|--------------------------------------------------------------------------
*/

export const formatEmployeeCurrency = (
    value
) => {

    return new Intl
        .NumberFormat(
            "en-IN",
            {
                style: "currency",
                currency: "INR",
                maximumFractionDigits: 2,
            }
        )
        .format(
            Number(
                value ||
                0
            )
        );

};


/*
|--------------------------------------------------------------------------
| UNIQUE OPTIONS
|--------------------------------------------------------------------------
*/

export const getUniqueOptions = (
    values = [],
    fallback = []
) => {

    return [
        ...new Set([
            ...(
                Array.isArray(values)
                    ? values
                    : []
            ),

            ...(
                Array.isArray(fallback)
                    ? fallback
                    : []
            ),
        ]),
    ]
        .filter(Boolean);

};


/*
|--------------------------------------------------------------------------
| PERMISSION FEATURE
|--------------------------------------------------------------------------
|
| employee.view
| => Employee
|
| employee.salary.view
| => Salary
|
| employee.advance.recover
| => Advance
|
|--------------------------------------------------------------------------
*/

export const getPermissionFeature = (
    permission
) => {

    const code =
        String(
            permission?.code ||
            ""
        )
            .trim()
            .toLowerCase();


    if (
        !code
    ) {
        return "General";
    }


    const parts =
        code.split(".");


    if (
        parts.length <=
        2
    ) {

        return formatEmployeeLabel(
            parts[0]
        );

    }


    return formatEmployeeLabel(
        parts
            .slice(
                1,
                -1
            )
            .join("_")
    );

};


/*
|--------------------------------------------------------------------------
| PERMISSION GROUP
|--------------------------------------------------------------------------
*/

export const buildPermissionGroups = (
    permissionModules = []
) => {

    if (
        !Array.isArray(
            permissionModules
        )
    ) {
        return [];
    }


    return permissionModules.map(
        (
            module
        ) => {

            const featureMap =
                {};


            const permissions =
                Array.isArray(
                    module?.permissions
                )
                    ? module.permissions
                    : [];


            permissions.forEach(
                (
                    permission
                ) => {

                    const feature =
                        getPermissionFeature(
                            permission
                        );


                    if (
                        !featureMap[
                            feature
                        ]
                    ) {

                        featureMap[
                            feature
                        ] = [];

                    }


                    featureMap[
                        feature
                    ].push(
                        permission
                    );

                }
            );


            return {
                ...module,

                features:
                    Object.entries(
                        featureMap
                    )
                        .map(
                            ([
                                name,
                                permissions,
                            ]) => ({
                                name,
                                permissions,
                            })
                        ),
            };

        }
    );

};


/*
|--------------------------------------------------------------------------
| ALL PERMISSION IDS
|--------------------------------------------------------------------------
*/

export const getAllPermissionIds = (
    groups = []
) => {

    return groups
        .flatMap(
            (
                module
            ) =>
                (
                    module?.features ||
                    []
                )
                    .flatMap(
                        (
                            feature
                        ) =>
                            (
                                feature?.permissions ||
                                []
                            )
                                .map(
                                    getObjectId
                                )
                    )
        )
        .filter(Boolean);

};


/*
|--------------------------------------------------------------------------
| LOGIN
|--------------------------------------------------------------------------
*/

export const hasEmployeeLoginAccount = (
    employee
) => {

    return Boolean(
        getObjectId(
            employee?.userId
        )
    );

};


export const isEmployeeLoginActive = (
    employee
) => {

    if (
        !employee?.userId ||
        typeof employee.userId !==
        "object"
    ) {

        return false;

    }


    return String(
        employee
            ?.userId
            ?.status ||
        ""
    )
        .trim()
        .toUpperCase() ===
        "ACTIVE";

};


/*
|--------------------------------------------------------------------------
| API ERROR
|--------------------------------------------------------------------------
*/

export const getEmployeeApiError = (
    error,
    fallback =
        "Something went wrong."
) => {

    return (
        error
            ?.response
            ?.data
            ?.message ||

        error
            ?.message ||

        fallback
    );

};